/**
 * ExportMenu Component
 *
 * A universal, reusable dropdown menu for exporting table data to CSV, Excel,
 * and PDF, and optionally exporting a chart element as a PNG or SVG image.
 *
 * Usage:
 *   <ExportMenu
 *     data={rows}
 *     filename="inventory_report"
 *     columns={[
 *       { key: 'sku', header: 'SKU' },
 *       { key: 'product_name', header: 'Product Name' },
 *     ]}
 *     title="Inventory Report"
 *     chartRef={myChartRef}          // optional
 *   />
 */
import React, { useState, RefObject } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Slideshow as PptIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  exportToCSV,
  exportToExcel,
  exportToPDF,
  exportToPPTX,
  exportChartAsImage,
  exportWithChart,
  autoColumns,
  ColumnDef,
} from '../../utils/exportUtils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ExportMenuProps {
  /** Data rows to export */
  data: Record<string, any>[];
  /** Base filename (date suffix is appended automatically) */
  filename: string;
  /** Optional column definitions. When omitted, columns are auto-detected. */
  columns?: ColumnDef[];
  /** Optional title printed on the PDF export header */
  title?: string;
  /** Optional subtitle printed on the PDF export header */
  subtitle?: string;
  /** Optional sheet name for Excel exports */
  sheetName?: string;
  /** Optional ref to a chart container element for image export */
  chartRef?: RefObject<HTMLElement>;
  /**
   * Optional custom Excel export handler. When provided, the menu's
   * "Export as Excel" item delegates to this function instead of the
   * built-in single-sheet exporter — useful for pages that need a
   * multi-sheet workbook (e.g. Dashboard).
   */
  customExcelHandler?: () => void | Promise<void>;
  /** When true the button is disabled */
  disabled?: boolean;
  /** MUI Button size */
  size?: 'small' | 'medium' | 'large';
  /** MUI Button variant */
  variant?: 'text' | 'outlined' | 'contained';
  /** Custom button label. Defaults to i18n "Export" */
  label?: string;
  /** Callback fired when an export starts (use to force-open collapsed sections) */
  onExportStart?: (format: string) => void;
  /** Callback fired after any export completes (for analytics / toast) */
  onExportComplete?: (format: string) => void;
  /** Callback fired on export error */
  onExportError?: (error: Error, format: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ExportMenu: React.FC<ExportMenuProps> = ({
  data,
  filename,
  columns,
  title,
  subtitle,
  sheetName,
  chartRef,
  customExcelHandler,
  disabled = false,
  size = 'small',
  variant = 'outlined',
  label,
  onExportStart,
  onExportComplete,
  onExportError,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  // Resolve columns: use provided or auto-detect from data
  const resolvedColumns = columns || autoColumns(data);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Wraps an export action with loading state and error handling.
  // When onExportStart is provided (e.g. to force-open collapsed sections),
  // waits for layout to settle before running the capture action.
  const runExport = async (format: string, action: () => void | Promise<void>) => {
    setExporting(format);
    handleClose();
    try {
      onExportStart?.(format);
      // If the caller needs to force-open collapsed sections so charts
      // become measurable, give the DOM time to settle (2 RAFs + 350ms).
      if (onExportStart && chartRef?.current) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(resolve, 350);
            });
          });
        });
      }
      await action();
      onExportComplete?.(format);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      // eslint-disable-next-line no-console
      console.error(`Export failed (${format}):`, error);
      onExportError?.(error, format);
    } finally {
      setExporting(null);
    }
  };

  const handleExportCSV = () =>
    runExport('csv', () => {
      exportToCSV(data, filename, resolvedColumns);
    });

  const handleExportExcel = () =>
    runExport('excel', async () => {
      if (customExcelHandler) {
        await customExcelHandler();
      } else if (chartRef?.current) {
        await exportWithChart(
          data,
          filename,
          title || filename,
          chartRef,
          sheetName || 'Data',
          resolvedColumns.map((c: ColumnDef) => ({ key: c.key, label: c.header, format: c.format })),
        );
      } else {
        exportToExcel(data, filename, sheetName || 'Data', resolvedColumns);
      }
    });

  const handleExportPDF = () =>
    runExport('pdf', async () => {
      await exportToPDF(data, filename, title || filename, resolvedColumns, chartRef);
    });

  const handleExportPPT = () =>
    runExport('pptx', async () => {
      await exportToPPTX(data, filename, title || filename, {
        subtitle,
        columns: resolvedColumns.map((c: ColumnDef) => ({ key: c.key, label: c.header, format: c.format })),
        chartRef,
      });
    });

  const handleExportChartPNG = () =>
    runExport('chart-png', async () => {
      if (!chartRef) return;
      await exportChartAsImage(chartRef, filename, 'png');
    });

  const handleExportChartSVG = () =>
    runExport('chart-svg', async () => {
      if (!chartRef) return;
      await exportChartAsImage(chartRef, filename, 'svg');
    });

  const isExporting = exporting !== null;
  const noData = !data || data.length === 0;
  // When a custom Excel handler is provided the menu is still actionable even
  // if `data` is empty (the handler may be sourcing from elsewhere).
  const buttonDisabled = noData && !customExcelHandler && !chartRef?.current;

  return (
    <>
      <Tooltip title={buttonDisabled ? 'No data to export' : ''}>
        <span>
          <Button
            variant={variant}
            size={size}
            disabled={disabled || isExporting || buttonDisabled}
            startIcon={
              isExporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            endIcon={!isExporting ? <ArrowDownIcon /> : undefined}
            onClick={handleClick}
            sx={{
              borderColor: 'var(--color-accent)',
              color: '#8B7355',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#B8941F',
                bgcolor: 'rgba(201, 169, 110, 0.06)',
              },
            }}
          >
            {isExporting
              ? 'Exporting...'
              : label || t('common.export', 'Export')}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            borderRadius: 2,
          },
        }}
      >
        {/* --- Data exports --- */}
        <MenuItem onClick={handleExportCSV} disabled={noData}>
          <ListItemIcon>
            <CsvIcon fontSize="small" sx={{ color: '#8B2438' }} />
          </ListItemIcon>
          <ListItemText>{t('common.exportCSV', 'Export as CSV')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportExcel} disabled={noData && !customExcelHandler}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" sx={{ color: '#217346' }} />
          </ListItemIcon>
          <ListItemText>{t('common.exportExcel', 'Export as Excel')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportPDF} disabled={noData}>
          <ListItemIcon>
            <PdfIcon fontSize="small" sx={{ color: '#DC2626' }} />
          </ListItemIcon>
          <ListItemText>{t('common.exportPDF', 'Export as PDF')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportPPT} disabled={noData && !chartRef?.current}>
          <ListItemIcon>
            <PptIcon fontSize="small" sx={{ color: '#D04423' }} />
          </ListItemIcon>
          <ListItemText>{t('common.exportPPT', 'Export as PowerPoint')}</ListItemText>
        </MenuItem>

        {/* --- Chart image exports (only shown when chartRef provided) --- */}
        {chartRef && (
          <>
            <Divider />
            <MenuItem onClick={handleExportChartPNG} disabled={!chartRef.current}>
              <ListItemIcon>
                <ImageIcon fontSize="small" sx={{ color: '#7B1FA2' }} />
              </ListItemIcon>
              <ListItemText>
                {t('common.exportChartPNG', 'Export Chart as PNG')}
              </ListItemText>
            </MenuItem>

            <MenuItem onClick={handleExportChartSVG} disabled={!chartRef.current}>
              <ListItemIcon>
                <ImageIcon fontSize="small" sx={{ color: '#00897B' }} />
              </ListItemIcon>
              <ListItemText>
                {t('common.exportChartSVG', 'Export Chart as SVG')}
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ExportMenu;
